import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Coin, X, ArrowRight } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { getCredits, isBetaBannerDismissed } from '../../lib/credits';
import { IS_BETA, BETA_FREE_CREDITS } from '../../lib/constants';
import { PAGE_ROUTE } from '../../lib/routes';
interface CreditsIndicatorProps {
  navigate: (page: string) => void;
  /** Bump this to force re-read after a credit consume/add elsewhere. */
  refreshKey?: number;
}
export function CreditsIndicator({
  navigate,
  refreshKey
}: CreditsIndicatorProps) {
  const [balance, setBalance] = useState<number>(() => getCredits());
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setBalance(getCredits());
  }, [refreshKey]);
  useEffect(() => {
    const handler = () => setBalance(getCredits());
    window.addEventListener('izio:credits-changed', handler);
    return () => window.removeEventListener('izio:credits-changed', handler);
  }, []);
  const openPopover = () => {
    if (btnRef.current) {
      setAnchorRect(btnRef.current.getBoundingClientRect());
    }
    setOpen(true);
  };
  const closePopover = () => setOpen(false);
  const isZero = balance === 0;
  const isLow = balance > 0 && balance <= 5;
  const pillCls = cn('inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full transition-colors cursor-pointer border', isZero ? 'bg-red-3/30 border-red-1 text-red-1' : isLow ? 'bg-dark-3 border-lime-1 text-lime-1' : 'bg-dark-3 border-transparent text-light-1 hover:bg-dark-4');
  const iconCls = cn('shrink-0', isZero ? 'text-red-1' : isLow ? 'text-lime-1 animate-pulse' : 'text-light-2');
  return <>
      <button ref={btnRef} onClick={open ? closePopover : openPopover} className={pillCls} aria-label={`${balance} credite`} aria-expanded={open} title={`${balance} credite`} data-id="element-62">
        <Coin size={18} weight={isLow || isZero ? 'fill' : 'regular'} className={iconCls} data-id="element-63" />
        <span className="text-sm font-semibold tabular-nums" data-id="element-64">{balance}</span>
      </button>

      {open && anchorRect && <CreditsPopover balance={balance} anchorRect={anchorRect} onClose={closePopover} onNavigatePricing={() => {
      closePopover();
      navigate(PAGE_ROUTE.PRICING);
    }} data-id="element-65" />}
    </>;
}
// --- Popover (anchored on desktop, full-screen drawer on mobile) ---
interface CreditsPopoverProps {
  balance: number;
  anchorRect: DOMRect;
  onClose: () => void;
  onNavigatePricing: () => void;
}
function CreditsPopover({
  balance,
  anchorRect,
  onClose,
  onNavigatePricing
}: CreditsPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window === 'undefined' ? false : window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const isZero = balance === 0;
  const isLow = balance > 0 && balance <= 5;
  const Content = <>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-dark-4 sm:border-b-0 sm:pb-0" data-id="element-66">
        <div data-id="element-67">
          <p className="text-xs uppercase tracking-wider text-light-3 font-medium" data-id="element-68">
            Sold credite
          </p>
          <p className={cn('text-3xl font-bold mt-0.5', isZero ? 'text-red-1' : isLow ? 'text-lime-1' : 'text-light-1')} data-id="element-69">
            {balance}
            <span className="text-base text-light-3 font-medium ml-1.5" data-id="element-70">
              {balance === 1 ? 'credit' : 'credite'}
            </span>
          </p>
        </div>
        {isMobile && <button onClick={onClose} className="text-light-3 hover:text-light-1 -m-1 p-1" aria-label="Închide" data-id="element-71">
            <X size={22} weight="bold" data-id="element-72" />
          </button>}
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-3" data-id="element-73">
        {isZero && <p className="text-sm text-light-2" data-id="element-74">
            Ai consumat toate creditele. Vezi pachetele disponibile pentru a
            continua să trimiți oferte chiriașilor.
          </p>}
        {!isZero && isLow && <p className="text-sm text-light-2" data-id="element-75">
            Mai ai doar{' '}
            <span className="font-semibold text-lime-1" data-id="element-76">{balance}</span>{' '}
            {balance === 1 ? 'credit' : 'credite'}. Aruncă o privire la pachete
            ca să nu rămâi fără.
          </p>}
        {!isZero && !isLow && <p className="text-sm text-light-2" data-id="element-77">
            🎁 Ai{' '}
            <span className="font-semibold text-lime-1" data-id="element-78">
              {balance} {balance === 1 ? 'credit gratuit' : 'credite gratuite'}
            </span>{' '}
            din pachetul de bun-venit.
          </p>}

        {IS_BETA && <p className="text-xs text-light-3 leading-relaxed" data-id="element-79">
            Platforma este momentan în beta. Folosește creditele gratuite ca să
            trimiți oferte — plățile vor fi disponibile la lansarea oficială.
          </p>}
      </div>

      {/* Footer actions */}
      <div className="px-5 pb-5 pt-1 flex flex-col gap-2" data-id="element-80">
        <button onClick={onNavigatePricing} className="w-full h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold text-sm inline-flex items-center justify-center gap-1.5 transition-colors" data-id="element-81">
          Vezi oferta
          <ArrowRight size={16} weight="bold" data-id="element-82" />
        </button>
      </div>
    </>;
  if (isMobile) {
    return createPortal(<div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" data-id="element-83">
        <div className="absolute inset-0 bg-dark-1/70 backdrop-blur-sm" onClick={onClose} data-id="element-84" />
        <div ref={panelRef} className="absolute inset-x-0 bottom-0 bg-dark-2 border-t border-dark-4 rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom duration-200" data-id="element-85">
          <div className="flex justify-center pt-3 pb-1" data-id="element-86">
            <div className="w-10 h-1 rounded-full bg-dark-4" data-id="element-87" />
          </div>
          {Content}
        </div>
      </div>, document.body);
  }
  const top = anchorRect.bottom + 8;
  const right = window.innerWidth - anchorRect.right;
  return createPortal(<div ref={panelRef} style={{
    position: 'fixed',
    top,
    right,
    width: 340,
    zIndex: 100
  }} className="bg-dark-2 border border-dark-4 rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-150" role="dialog" aria-modal="false" data-id="element-88">
      {Content}
    </div>, document.body);
}